// src/pages/notifications/BroadcastHistory.jsx

import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';

const BroadcastHistory = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    fetchBroadcasts();
  }, [currentPage]);

  const fetchBroadcasts = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getBroadcasts({ page: currentPage, limit: 20 });
      if (response.data.success) {
        setBroadcasts(response.data.data.broadcasts);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch broadcasts', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

const handleClearAll = async () => {
  try {
    console.log('🗑️ Clearing broadcast history...');
    
    const response = await notificationsAPI.clearBroadcasts();
    
    console.log('✅ Clear response:', response);
    
    setConfirmClear(false);
    
    // ✅ FIX: Clear broadcasts immediately in state
    setBroadcasts([]);
    setTotalPages(1);
    setCurrentPage(1);
    
    setToast({ 
      show: true, 
      message: 'Broadcast history cleared successfully', 
      type: 'success' 
    });
    
    // ✅ Then reload to confirm
    await fetchBroadcasts();
    
  } catch (error) {
    console.error('❌ Clear error:', error);
    console.error('Error response:', error.response?.data);
    
    setToast({ 
      show: true, 
      message: error.response?.data?.message || 'Failed to clear history. Check your permissions.',
      type: 'error' 
    });
  }
};

  // ✅ FIX: Helper to get target audience label
  const getTargetAudience = (broadcast) => {
    if (broadcast.target_type === 'all') return 'All Users';
    if (broadcast.target_type === 'specific') return `${broadcast.user_ids?.length || 0} Selected Users`;
    return 'Segment';
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Broadcast History</h2>
            <p className="page-subtitle">View sent notifications</p>
          </div>
          {broadcasts.length > 0 && (
            <button
              className="btn btn-outline-danger"
              onClick={() => setConfirmClear(true)}
            >
              <i className="bi bi-trash me-2"></i>Clear All
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Sent At</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Audience</th>
                  <th>Sent Count</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No broadcasts sent yet</p>
                    </td>
                  </tr>
                ) : (
                  broadcasts.map((broadcast) => (
                    <tr key={broadcast.id}>
                      <td><small>{broadcast.sent_at ? new Date(broadcast.sent_at).toLocaleString() : 'Scheduled'}</small></td>
                      <td>{broadcast.title}</td>
                      <td><span className="badge badge-purple">{broadcast.type}</span></td>
                      <td>{getTargetAudience(broadcast)}</td>
                      {/* ✅ FIX: Use total_sent not sent_count */}
                      <td>{broadcast.total_sent || 0}</td>
                      <td>
                        <span className={`badge ${broadcast.status === 'sent' ? 'badge-success' :
                            broadcast.status === 'sending' ? 'badge-warning' :
                              broadcast.status === 'scheduled' ? 'badge-info' :
                                'badge-secondary'
                          }`}>
                          {broadcast.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <ConfirmModal
              show={confirmClear}
              onClose={() => setConfirmClear(false)}
              onConfirm={handleClearAll}
              title="Clear Broadcast History"
              message="Are you sure you want to clear all broadcast history? This action cannot be undone."
              type="danger"
            />
          </div>

          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BroadcastHistory;